'use client';

import React, { useState } from 'react';

const subjectOptions = [
    'General Inquiry',
    'Partnership',
    'Product Demo — Wisly',
    'Product Demo — CapState',
    'Press & Media',
    'Technology Solutions',
];

export default function ContactForm() {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: '',
    });
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        setLoading(true);

        try {
            const response = await fetch(
                `${process.env.NEXT_PUBLIC_API_URL}/api/contact`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(formData),
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(
                    data.message || 'Unable to submit your message.'
                );
            }

            setSubmitted(true);

        } catch (error) {
            console.error(
                'Contact submission error:',
                error
            );

            alert(
                error instanceof Error
                    ? error.message
                    : 'Something went wrong. Please try again.'
            );
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <div className="glass-card rounded-2xl p-12 flex flex-col items-center justify-center text-center min-h-[400px]">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                        <path d="M6 14l5.5 5.5L22 8" stroke="var(--primary)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
                <h3 className="text-2xl font-700 text-foreground mb-3 tracking-tight">Message received</h3>
                <p className="text-muted-foreground leading-relaxed max-w-sm">
                    Thanks for reaching out. Our team will get back to you within 1–2 business days.
                </p>
                <button
                    onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', subject: '', message: '' }); }}
                    className="mt-8 btn-secondary text-sm"
                >
                    Send another message
                </button>
            </div>
        );
    }

    return (
        <div>
            <h2 className="text-2xl font-700 text-foreground tracking-tight mb-2">Send us a message</h2>
            <p className="text-sm text-muted-foreground mb-8">Fill in the form and we'll get back to you promptly.</p>

            <form onSubmit={handleSubmit} className="space-y-5">
                {/* Name + Email row */}
                <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                        <label className="section-label text-muted-foreground mb-2 block" htmlFor="name">
                            Full name
                        </label>
                        <input
                            id="name"
                            name="name"
                            type="text"
                            required
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Amara Osei"
                            className="w-full h-12 bg-input border border-border rounded-xl px-4 text-foreground placeholder-muted-foreground text-sm focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 transition-all"
                        />
                    </div>
                    <div>
                        <label className="section-label text-muted-foreground mb-2 block" htmlFor="email">
                            Email address
                        </label>
                        <input
                            id="email"
                            name="email"
                            type="email"
                            required
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="amara@example.com"
                            className="w-full h-12 bg-input border border-border rounded-xl px-4 text-foreground placeholder-muted-foreground text-sm focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 transition-all"
                        />
                    </div>
                </div>

                {/* Subject */}
                <div>
                    <label className="section-label text-muted-foreground mb-2 block" htmlFor="subject">
                        Subject
                    </label>
                    <select
                        id="subject"
                        name="subject"
                        required
                        value={formData.subject}
                        onChange={handleChange}
                        className="w-full h-12 bg-input border border-border rounded-xl px-4 text-foreground text-sm focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 transition-all appearance-none cursor-pointer"
                        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%238A8FA8' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center' }}
                    >
                        <option value="" disabled>Select a subject...</option>
                        {subjectOptions.map((opt) => (
                            <option key={opt} value={opt} style={{ background: '#111318' }}>{opt}</option>
                        ))}
                    </select>
                </div>

                {/* Message */}
                <div>
                    <label className="section-label text-muted-foreground mb-2 block" htmlFor="message">
                        Message
                    </label>
                    <textarea
                        id="message"
                        name="message"
                        required
                        rows={6}
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us what you're working on or what you'd like to discuss..."
                        className="w-full bg-input border border-border rounded-xl px-4 py-3.5 text-foreground placeholder-muted-foreground text-sm focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 transition-all resize-none"
                    />
                </div>

                <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                >
                    {loading ? (
                        <>
                            <svg className="animate-spin" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" strokeDasharray="20" strokeDashoffset="10" />
                            </svg>
                            Sending...
                        </>
                    ) : (
                        <>
                            Send message
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </>
                    )}
                </button>
            </form>
        </div>
    );
}